import { differenceInDays, format, isAfter } from "date-fns";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useFormatting } from "@/hooks/useFormatting";

interface DelayBillModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (adjustment: any) => void;
}

// Mock bills
const MOCK_BILLS = [
  {
    id: "B001",
    supplier: "Office Supplies Ltd",
    amount: 3500,
    dueDate: new Date("2025-11-25"),
  },
  {
    id: "B002",
    supplier: "Cloud Services Inc",
    amount: 8200,
    dueDate: new Date("2025-11-28"),
  },
  {
    id: "B003",
    supplier: "Marketing Agency",
    amount: 12000,
    dueDate: new Date("2025-12-01"),
  },
];

export function DelayBillModal({
  open,
  onOpenChange,
  onAdd,
}: DelayBillModalProps) {
  const { formatCurrency } = useFormatting();
  const [selectedBill, setSelectedBill] = useState<string>("");
  const [newDate, setNewDate] = useState<Date>();
  const [reason, setReason] = useState("");

  const bill = MOCK_BILLS.find((b) => b.id === selectedBill);
  const currentDate = bill?.dueDate;

  const daysDifference =
    currentDate && newDate ? differenceInDays(newDate, currentDate) : 0;
  const isValid =
    selectedBill && newDate && currentDate && isAfter(newDate, currentDate);

  useEffect(() => {
    if (!open) {
      setSelectedBill("");
      setNewDate(undefined);
      setReason("");
    }
  }, [open]);

  const handleAdd = () => {
    if (!isValid || !bill) return;

    onAdd({
      type: "delay-bill",
      description: `Delay Bill ${bill.id} - ${bill.supplier}`,
      amount: -bill.amount,
      originalDate: currentDate,
      newDate: newDate,
      reason,
      impact: `Delays £${(bill.amount / 1000).toFixed(0)}K outflow by ${daysDifference} days`,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delay Bill Payment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select Bill */}
          <div className="space-y-2">
            <Label>Select Bill *</Label>
            <Select value={selectedBill} onValueChange={setSelectedBill}>
              <SelectTrigger>
                <SelectValue placeholder="Search bills..." />
              </SelectTrigger>
              <SelectContent>
                {MOCK_BILLS.map((bill) => (
                  <SelectItem key={bill.id} value={bill.id}>
                    Bill {bill.id} - {bill.supplier} -{" "}
                    {formatCurrency(bill.amount)} (Due{" "}
                    {format(bill.dueDate, "MMM dd")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current Due Date */}
          {currentDate && (
            <div className="space-y-2">
              <Label>Current Due Date</Label>
              <Input value={format(currentDate, "MMM dd, yyyy")} disabled />
            </div>
          )}

          {/* New Due Date */}
          <div className="space-y-2">
            <Label>New Due Date *</Label>
            <DatePicker
              value={newDate}
              onChange={setNewDate}
              dateFormat="MMM dd, yyyy"
              placeholder="Pick a date"
              disabledDate={(date) =>
                currentDate ? !isAfter(date, currentDate) : false
              }
            />
            {currentDate && newDate && !isAfter(newDate, currentDate) && (
              <p className="text-sm text-red-500">
                New date must be later than current due date
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              placeholder="e.g., Negotiated extended payment terms"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          {/* Impact Preview */}
          {isValid && bill && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-sm font-medium">Impact Preview</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Cash impact on {format(currentDate!, "MMM dd")}:</span>
                  <span className="text-green-500">
                    +{formatCurrency(bill.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Cash impact on {format(newDate!, "MMM dd")}:</span>
                  <span className="text-red-500">
                    -{formatCurrency(bill.amount)}
                  </span>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">
                    Net effect: Delays £{(bill.amount / 1000).toFixed(0)}K
                    outflow by {daysDifference} days
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!isValid}>
              Add Adjustment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
